import type { ReactNode } from 'react'
import { HexNut } from '~/components/brand'
import { TourLayer } from '~/components/tour/layer'
import { Spinner } from '~/components/ui/primitives'
import { useSyncAccountLocale } from '~/lib/i18n'
import {
  SessionContext,
  useBootstrapStatus,
  useRememberLocale,
  useSessionQuery,
} from '~/lib/session'
import { TourProvider } from '~/lib/tour/state'
import { ChangePasswordPage } from '~/routes/change-password'
import { LoginPage } from '~/routes/login'
import { SetupPage } from '~/routes/setup'
import { AppShell } from './shell'

export function AuthGate({ children }: { children: ReactNode }) {
  const session = useSessionQuery()
  const user = session.data?.user ?? null
  // Device and account agree on a language after this, in whichever direction the
  // one that was actually chosen points.
  useSyncAccountLocale(user?.locale, useRememberLocale())
  // Only ask whether setup is needed once we know nobody is signed in.
  const bootstrap = useBootstrapStatus(session.isSuccess && !user)

  if (session.isPending || (session.isSuccess && !user && bootstrap.isPending)) {
    return (
      <div className="blueprint flex h-full flex-col items-center justify-center gap-5">
        <HexNut className="size-12 animate-pulse-soft" />
        <Spinner className="size-5" />
      </div>
    )
  }

  if (!user) {
    return bootstrap.data?.needed ? <SetupPage /> : <LoginPage />
  }

  // Temporary passwords must be replaced before anything else. The server enforces
  // this too — every route but this one returns `password_change_required`.
  if (user.mustChangePassword) {
    return <ChangePasswordPage />
  }

  /**
   * The walkthrough sits between the session and the shell: it needs to know who
   * this is, and it has to outlive every screen it walks through. Nothing of it is
   * downloaded until it opens.
   */
  return (
    <SessionContext value={user}>
      <TourProvider>
        <AppShell>{children}</AppShell>
        <TourLayer />
      </TourProvider>
    </SessionContext>
  )
}
